/**
 * The wall-clock time a schedule fires, as people read and enter it.
 *
 * The API names its hour field `hourUtc` for compatibility, but it holds the
 * hour on the clock of the schedule's own timezone, with `minuteLocal` beside
 * it (backend/app/core/report_schedule_time.py). The Reporting page already
 * shows and takes "9:05 AM" local to the timezone; the schedule modal printed
 * `${hourUtc}:00` and asked for an "Hour (UTC)", so a person typing 14 for
 * two o'clock UTC got 2 pm New York time, and a 9:05 schedule read "9:00".
 */

/** 9:05 AM — the time a schedule fires, on the clock of its own zone. */
export function clockLabel(hour: number, minute: number | null | undefined): string {
  const h = Number.isFinite(hour) ? hour : 12
  const m = Number.isFinite(minute ?? NaN) ? Number(minute) : 0
  const twelve = h % 12 === 0 ? 12 : h % 12
  return `${twelve}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/** The value an `<input type="time">` takes: "09:05". */
export function timeInputValue(hour: number, minute: number | null | undefined): string {
  const h = Number.isFinite(hour) ? Math.min(Math.max(Math.trunc(hour), 0), 23) : 12
  const m = Number.isFinite(minute ?? NaN) ? Math.min(Math.max(Math.trunc(Number(minute)), 0), 59) : 0
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** "09:05" -> { hour: 9, minute: 5 }; null when it is not a time of day. */
export function parseTimeInput(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  return hour <= 23 && minute <= 59 ? { hour, minute } : null
}
